import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert,
} from '@mui/material';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { learningAnalyticsAPI, SeasonalPatternData } from '../../services/learningAnalyticsApi';

// Color scale for heatmap cells
const getHeatmapColor = (rate: number): string => {
  if (rate >= 90) return '#1b5e20';
  if (rate >= 80) return '#388e3c';
  if (rate >= 70) return '#66bb6a';
  if (rate >= 60) return '#fbc02d';
  if (rate >= 50) return '#f57c00';
  return '#d32f2f';
};

const getHeatmapTextColor = (rate: number): string => {
  if (rate >= 80 || rate < 50) return '#fff';
  return '#333';
};

const SeasonalPatternsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SeasonalPatternData | null>(null);
  const [district, setDistrict] = useState<string>('');
  const [level, setLevel] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (district) params.district = district;
      if (level) params.level = parseInt(level);

      const response = await learningAnalyticsAPI.getSeasonalPatterns(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load seasonal pattern data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [district, level]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, monthly_student, dow_student, heatmap } = data;

  // Build monthly chart data with year-month labels
  const monthlyChartData = monthly_student.map((m) => ({
    ...m,
    label: `${m.year}-${String(m.month_num).padStart(2, '0')}`,
  }));

  // Build heatmap grid: rows = months, columns = days
  const months = Array.from(new Set(heatmap.map(h => h.month))).sort((a, b) => {
    const aNum = heatmap.find(h => h.month === a)?.month_num ?? 0;
    const bNum = heatmap.find(h => h.month === b)?.month_num ?? 0;
    return aNum - bNum;
  });
  const days = Array.from(new Set(heatmap.map(h => h.day))).sort((a, b) => {
    const aIdx = heatmap.find(h => h.day === a)?.day_index ?? 0;
    const bIdx = heatmap.find(h => h.day === b)?.day_index ?? 0;
    return aIdx - bIdx;
  });

  const heatmapLookup: Record<string, number> = {};
  heatmap.forEach(h => {
    heatmapLookup[`${h.month}-${h.day}`] = h.attendance_rate;
  });

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="School Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Best Month
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#388e3c' }}>
                {summary.best_month}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {summary.best_month_rate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Worst Month
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#d32f2f' }}>
                {summary.worst_month}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {summary.worst_month_rate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Best Day
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#388e3c' }}>
                {summary.best_day}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {summary.best_day_rate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Worst Day
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#d32f2f' }}>
                {summary.worst_day}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {summary.worst_day_rate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Monthly Attendance Line Chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Monthly Student Attendance Trend</Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis domain={[0, 100]} label={{ value: 'Attendance %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    formatter={(value: number) => [`${value.toFixed(1)}%`, 'Attendance Rate']}
                    labelFormatter={(label: string) => `Period: ${label}`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="attendance_rate" stroke="#1976d2" strokeWidth={2}
                    name="Attendance Rate" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Day-of-Week Bar Chart */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Attendance by Day of Week</Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={dow_student}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} label={{ value: 'Attendance %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    formatter={(value: number) => [`${value.toFixed(1)}%`, 'Attendance Rate']}
                  />
                  <Legend />
                  <Bar dataKey="student_attendance_rate" fill="#1976d2" name="Attendance Rate" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Heatmap Table: Month x Day grid */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Attendance Heatmap (Month x Day)</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Colored cells show average attendance rates by month and day of week.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Month</TableCell>
                  {days.map((day) => (
                    <TableCell key={day} align="center" sx={{ fontWeight: 'bold' }}>
                      {day}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {months.map((month) => (
                  <TableRow key={month}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{month}</TableCell>
                    {days.map((day) => {
                      const rate = heatmapLookup[`${month}-${day}`];
                      return (
                        <TableCell
                          key={`${month}-${day}`}
                          align="center"
                          sx={{
                            backgroundColor: rate != null ? getHeatmapColor(rate) : '#f5f5f5',
                            color: rate != null ? getHeatmapTextColor(rate) : '#999',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            minWidth: 60,
                          }}
                        >
                          {rate != null ? `${rate.toFixed(1)}%` : '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SeasonalPatternsTab;
